import type { InjectionKey, Ref } from 'vue'
import type { z } from 'zod'

/**
 * 单个字段校验规则
 */
export interface FormRule {
    /** 是否必填 */
    required?: boolean
    /** 必填提示信息 */
    message?: string
    /** 正则校验 */
    pattern?: RegExp
    /** 最小长度 */
    min?: number
    /** 最大长度 */
    max?: number
    /** 校验触发时机 */
    trigger?: 'blur' | 'change' | 'submit'
    /** 自定义校验函数 */
    validator?: (value: any, rule: FormRule) => boolean | string | Promise<boolean | string>
}

/**
 * 表单校验规则集合
 */
export type FormRules<T = any> = {
    [K in keyof T]?: FormRule | FormRule[]
}

/**
 * SForm Props
 */
export interface FormProps<T = Record<string, any>> {
    /** 表单数据对象 */
    model: T
    /** 表单校验规则 */
    rules?: FormRules<T>
    /** Zod Schema（可选，优先级高于 rules） */
    schema?: z.ZodType<T>
    /** 标签宽度 */
    labelWidth?: string | number
    /** 标签位置 */
    labelPosition?: 'left' | 'right' | 'top'
    /** 是否禁用 */
    disabled?: boolean
    /** 是否显示必填星号 */
    showRequiredAsterisk?: boolean
    /** 校验失败时是否滚动到第一个错误 */
    scrollToError?: boolean
    /** 校验时是否将纯空格视为空值（默认 true） */
    trimWhitespace?: boolean
}

/**
 * SFormItem Props
 */
export interface FormItemProps {
    /** 字段名（对应 model 的 key） */
    prop?: string
    /** 标签文本 */
    label?: string
    /** 标签宽度（覆盖 Form 的设置） */
    labelWidth?: string | number
    /** 是否必填 */
    required?: boolean
    /** 单独的校验规则（覆盖 Form 的 rules） */
    rules?: FormRule | FormRule[]
    /** 错误信息（外部传入） */
    error?: string
    /** 是否显示错误信息 */
    showMessage?: boolean
}

/**
 * 表单校验结果
 */
export interface ValidateResult {
    valid: boolean
    errors: Record<string, string>
    fields: Record<string, { valid: boolean; message: string }>
}

/**
 * 表单上下文（provide/inject）
 */
export interface FormContext {
    model: Ref<Record<string, any>>
    rules: Ref<FormRules | undefined>
    labelWidth: Ref<string | number | undefined>
    labelPosition: Ref<'left' | 'right' | 'top'>
    disabled: Ref<boolean>
    showRequiredAsterisk: Ref<boolean>
    trimWhitespace: Ref<boolean>
    /** 注册表单项 */
    registerField: (field: FormItemContext) => void
    /** 注销表单项 */
    unregisterField: (prop: string) => void
    /** 校验单个字段 */
    validateField: (prop: string) => Promise<{ valid: boolean; message: string }>
    /** 获取字段错误 */
    getFieldError: (prop: string) => string
    /** 设置字段错误 */
    setFieldError: (prop: string, message: string) => void
    /** 清除字段错误 */
    clearFieldError: (prop: string) => void
}

/**
 * 表单项上下文
 */
export interface FormItemContext {
    prop: string
    validate: () => Promise<{ valid: boolean; message: string }>
    resetField: () => void
    clearValidate: () => void
}

/**
 * SForm 暴露的方法
 */
export interface FormExpose {
    /** 校验整个表单 */
    validate: () => Promise<ValidateResult>
    /** 校验指定字段 */
    validateField: (props: string | string[]) => Promise<ValidateResult>
    /** 重置表单（值 + 校验状态） */
    resetFields: () => void
    /** 清除校验状态 */
    clearValidate: (props?: string | string[]) => void
    /** 滚动到指定字段 */
    scrollToField: (prop: string) => void
}

/**
 * SFormItem 暴露的方法
 */
export interface FormItemExpose {
    /** 校验当前字段 */
    validate: () => Promise<{ valid: boolean; message: string }>
    /** 重置字段 */
    resetField: () => void
    /** 清除校验状态 */
    clearValidate: () => void
}

/** Form 注入 Key */
export const FORM_CONTEXT_KEY: InjectionKey<FormContext> = Symbol('SFormContext')
